/**
 * 预订返还
 */
import React, { useImperativeHandle, useState } from 'react';
import { Drawer, Spin, Button, message } from 'antd';
import intl from 'react-intl-universal';

import { DrawerHeader, DrawerFooter } from '@/components/PortalDrawer';
import ThrottleButton from '@/components/Reservation/ThrottleButton';
import usePortalModal from '@/components/PortalConfirm';
import { rollbackDirectRenewal } from '@/api/orderEdit';

import OrderInfo from './components/OrderInfo';
import CustomerInfo from './components/CustomerInfo';
import RentTimeInfo from './components/RentTimeInfo';
import ChannelInfo from './components/ChannelInfo';
import RoomInfo from './components/RoomInfo';
import BillInfo from './components/BillInfo';
import RemarkInfo from './components/RemarkInfo';

import { RollbackData, DetailContext, checkOrderStatus, getOrderDetail } from './helper';

import styles from './index.module.scss';

interface DrawerConfig {
  id: number;
}

export interface RollbackDrawerRef {
  openModal: (data: Partial<DrawerConfig>) => void;
}

interface IProps {
  innerRef: React.Ref<RollbackDrawerRef>;
  onUpdate: () => void;
}

const Index: React.FC<IProps> = (props) => {
  const [visible, setVisible] = useState(false);
  const [isLoading, setLoadingStatus] = useState(false);
  const [detailData, setDetailData] = useState<RollbackData>(null);
  const confirmPortal = usePortalModal();

  const closeModal = () => {
    setVisible(false);
  };

  // 提交
  const handleSubmitClick = async () => {
    setLoadingStatus(true);
    try {
      await rollbackDirectRenewal(detailData.extensionId);
      message.success(intl.get('reservation_cmp_extension_record_message_rollback_success'));
      setVisible(false);
      props.onUpdate();
    } catch (_) {
      //
    }
    setLoadingStatus(false);
  };

  // 初始化
  const initModalData: RollbackDrawerRef['openModal'] = async (config) => {
    const extensionId = config.id;
    if (extensionId) {
      setDetailData(null);
      try {
        const [, orderDetail] = await Promise.all([
          checkOrderStatus(extensionId, confirmPortal),
          getOrderDetail(extensionId),
        ]);
        setVisible(true);
        setDetailData(orderDetail);
      } catch (_) {
        //
      }
    }
  };

  // 抽屉主体内容
  const renderDrawerContent = () => {
    if (detailData) {
      return (
        <DetailContext.Provider value={detailData}>
          <OrderInfo />
          <CustomerInfo />
          <RentTimeInfo />
          <ChannelInfo />
          <RoomInfo />
          <BillInfo />
          <RemarkInfo />
        </DetailContext.Provider>
      );
    }
    return null;
  };

  useImperativeHandle(
    props.innerRef,
    () => ({
      openModal: initModalData,
    }),
    [],
  );

  return (
    <Drawer closable={false} destroyOnClose className={styles.detailDrawer} open={visible}>
      <Spin spinning={isLoading}>
        <DrawerHeader title={intl.get('drawer_extension_rollback_title')} onClose={closeModal} />
        <div className={styles.contentWrapper}>{renderDrawerContent()}</div>
        <DrawerFooter>
          <ThrottleButton type="primary" throttleTime={1000} onClick={handleSubmitClick}>
            {intl.get('drawer_extension_rollback_submit')}
          </ThrottleButton>
          <Button type="default" onClick={closeModal}>
            {intl.get('app_confirm_cancel_btn')}
          </Button>
        </DrawerFooter>
      </Spin>
    </Drawer>
  );
};

export default Index;
